import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

/**
 * Generic client-side table sorting.
 *   const { sorted, sortKey, sortDir, toggle } = useTableSort(rows, getters, "date", "asc");
 * getters: { key: (row) => value }  (strings compared pt-PT, numbers numerically;
 * null/undefined always last).
 */
export function useTableSort(rows, getters, defaultKey = null, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  function toggle(key, firstDir) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(firstDir || "asc");
  }

  const sorted = useMemo(() => {
    const list = [...(rows || [])];
    const get = sortKey && getters ? getters[sortKey] : null;
    if (!get) return list;
    const sign = sortDir === "asc" ? 1 : -1;
    return list
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const va = get(a.r);
        const vb = get(b.r);
        const na = va === null || va === undefined || va === "";
        const nb = vb === null || vb === undefined || vb === "";
        if (na !== nb) return na ? 1 : -1;
        let c = 0;
        if (!na) c = typeof va === "string" ? va.localeCompare(vb, "pt", { numeric: true }) : va - vb;
        return c !== 0 ? c * sign : a.i - b.i;
      })
      .map((x) => x.r);
  }, [rows, getters, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggle };
}

/** Clickable header cell. `firstDir` = direction on first click (default: desc for numbers). */
export function SortHeader({ k, label, sort, align = "left", className = "", firstDir = "desc", testid }) {
  const active = sort.sortKey === k;
  return (
    <th className={`${align === "right" ? "text-right" : "text-left"} font-medium ${className}`}>
      <button
        type="button"
        onClick={() => sort.toggle(k, firstDir)}
        data-testid={testid || `sort-${k}`}
        className={`inline-flex items-center gap-1 whitespace-nowrap [text-transform:inherit] hover:text-foreground ${active ? "text-foreground" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        {active ? (sort.sortDir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );
}

/** Lowercase + strip accents, for accent-insensitive search ("marco" finds "Março"). */
export function norm(s) {
  return String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}
